import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Bike, Banknote, TrendingUp, Clock, AlertCircle, Package } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import BikerLayout from '../../components/biker/BikerLayout';

const BikerDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bikerProfile, setBikerProfile] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      
      // Fetch biker profile
      const profileResponse = await api.get('/bikers/my-profile');
      setBikerProfile(profileResponse.data.biker);

      // Fetch earnings
      const earningsResponse = await api.get('/bikers/earnings');
      setEarnings(earningsResponse.data.earnings);

      // Fetch analytics
      const analyticsResponse = await api.get('/bikers/analytics');
      setStats(analyticsResponse.data.analytics);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to load dashboard',
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  const handleGoOnline = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      await api.put('/bikers/status', { isOnline: true });
      
      toast({
        title: 'You are now online!',
        description: 'You will start receiving delivery requests',
      });
      
      setBikerProfile({ ...bikerProfile, isOnline: true });
    } catch (error) {
      console.error('Error going online:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to go online',
        variant: 'destructive',
      });
    }
  };

  const handleGoOffline = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      await api.put('/bikers/status', { isOnline: false });
      
      toast({
        title: 'You are now offline',
        description: 'You will not receive new delivery requests',
      });
      
      setBikerProfile({ ...bikerProfile, isOnline: false });
    } catch (error) {
      console.error('Error going offline:', error);
      toast({
        title: 'Error',
        description: 'Failed to go offline',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <BikerLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
        </div>
      </BikerLayout>
    );
  }

  const getStatusBadge = () => {
    switch (bikerProfile?.approvalStatus) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-800">Approved</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800">Pending Approval</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800">Rejected</Badge>;
      case 'suspended':
        return <Badge className="bg-gray-100 text-gray-800">Suspended</Badge>;
      default:
        return null;
    }
  };

  return (
    <BikerLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Biker Dashboard</h1>
            <p className="text-gray-600 mt-1">Welcome back, {bikerProfile?.name}!</p>
          </div>
          {getStatusBadge()}
        </div>

        {/* Pending Approval Message */}
        {bikerProfile?.approvalStatus === 'pending' && (
          <Card className="p-6 bg-yellow-50 border-yellow-200">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-6 w-6 text-yellow-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-yellow-900">Application Under Review</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  Your biker application is currently being reviewed by our team. You'll be notified once approved.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Suspended Message */}
        {bikerProfile?.approvalStatus === 'suspended' && (
          <Card className="p-6 bg-red-50 border-red-200">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-6 w-6 text-red-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-900">Account Suspended</h3>
                <p className="text-sm text-red-700 mt-1">
                  {bikerProfile?.suspensionReason || 'Your account has been temporarily suspended. Please contact support for more information.'}
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Online/Offline Toggle */}
        {bikerProfile?.approvalStatus === 'approved' && (
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Online Status</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {bikerProfile?.isOnline ? 'You are currently online and receiving delivery requests' : 'Go online to start receiving delivery requests'}
                </p>
              </div>
              <Button
                onClick={bikerProfile?.isOnline ? handleGoOffline : handleGoOnline}
                className={bikerProfile?.isOnline ? 'bg-gray-600 hover:bg-gray-700' : 'bg-orange-600 hover:bg-orange-700'}
                size="lg"
              >
                {bikerProfile?.isOnline ? 'Go Offline' : 'Go Online'}
              </Button>
            </div>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Deliveries</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.totalDeliveries || 0}</p>
              </div>
              <Package className="h-8 w-8 text-orange-600" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Today's Earnings</p>
                <p className="text-2xl font-bold text-green-600">K{earnings?.todayEarnings?.toFixed(2) || '0.00'}</p>
              </div>
              <Banknote className="h-8 w-8 text-green-600" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Earnings</p>
                <p className="text-2xl font-bold text-purple-600">K{earnings?.totalEarnings?.toFixed(2) || '0.00'}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-purple-600" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Average Rating</p>
                <p className="text-2xl font-bold text-yellow-600">{bikerProfile?.rating?.toFixed(1) || '5.0'} ⭐</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-600" />
            </div>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/biker/deliveries')}>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <Package className="h-6 w-6 text-orange-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">Available Deliveries</h3>
                <p className="text-sm text-gray-600">View and accept delivery requests</p>
              </div>
            </div>
          </Card>

          <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/biker/wallet')}>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <Banknote className="h-6 w-6 text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">Wallet</h3>
                <p className="text-sm text-gray-600">Balance: K{earnings?.walletBalance?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </BikerLayout>
  );
};

export default BikerDashboard;
